import React, { useRef, useCallback, useEffect } from "react";
import Input from "../../Atomos/Inputs/Input";
import CatalogFilterButtons from "../CatalogFilterButtons";
import DDMultiple, { Option } from "../../Atomos/DropDown/DDMultiple";
import CalendarInput from "../../Atomos/Inputs/CalendarInput";
import { convertDateRangeToEpoch } from "../../../utils/functions";

export interface IFilterData {
  name: string;
  fieldName?: string; //Default name in lowercase
  type?: "text" | "date" | "multiselect" | "hidden" | "custom"; //Default multiselect
  /** When type is `custom`, render inline with other filters (e.g. date flights picker). */
  render?: () => React.ReactNode;
  objectData?: Option[];
  disabled?: boolean;
  comparison?:
    | "CONTAINS"
    | "BY_NAME"
    | "BY_ID"
    | "EQUAL"
    | "ARRAY_SEARCH_ID"
    | "ARRAY_CONTAINS"
    | "BOOLEAN"
    | "BETWEEN"
    | "IN"
    | "BETWEEN_TS"
    | "BETWEEN_TS_PRESET"
    | "NOT_EQUAL_OR_NULL"
    | "NOT_IN";
  getPresetRange?: (optionId: string) => string;
  disabledWrite?: boolean;
  customTitle?: string;
  disableSort?: boolean;
}

interface IContentProps {
  filterValues: any;
  setFilterValues: React.Dispatch<React.SetStateAction<any>>;
  filtersData: IFilterData[];
  clearSelections: () => void;
  Search: () => void;
  valueForQuery?: any;
  setValueForQuery?: React.Dispatch<React.SetStateAction<any>>;
  persistentFilter?: string[];
}

const Filters: React.FC<IContentProps> = ({
  filterValues,
  setFilterValues,
  filtersData,
  clearSelections,
  Search,
  setValueForQuery,
  persistentFilter,
}) => {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const userInteractionCountRef = useRef(0);
  const lastProcessedCountRef = useRef(0);

  useEffect(() => {
    if (userInteractionCountRef.current === lastProcessedCountRef.current) {
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const currentCount = userInteractionCountRef.current;
    
    debounceTimerRef.current = setTimeout(() => {
      if (currentCount === userInteractionCountRef.current) {
        lastProcessedCountRef.current = currentCount;
        Search();
      }
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filterValues, Search]);

  const handleFilters = (
    field: string,
    value: any,
    fieldName?: string,
    comparison?: string,
    getPresetRange?: (optionId: string) => string,
  ) => {
    const _field = fieldName ?? field.toLocaleLowerCase();

    userInteractionCountRef.current += 1;

    setFilterValues((prev: any) => ({ ...prev, [field]: value }));
    buildQueryValue(
      _field,
      value,
      comparison as ComparisonType,
      getPresetRange,
    );
  };

  type ComparisonType =
    | "CONTAINS"
    | "BY_NAME"
    | "BY_ID"
    | "EQUAL"
    | "ARRAY_SEARCH_ID"
    | "ARRAY_CONTAINS"
    | "BOOLEAN"
    | "BETWEEN"
    | "BETWEEN_TS"
    | "BETWEEN_TS_PRESET"
    | "IN"
    | "NOT_EQUAL_OR_NULL"
    | "NOT_IN";
  interface QueryValue {
    value: string | string[];
    comparisonType?: string;
    arraySearchField?: string;
  }

  const buildQueryValue = (
    fieldName: string,
    value: any,
    comparison: ComparisonType = "BY_ID",
    getPresetRange?: (optionId: string) => string,
  ): void => {
    const comparisonMap: Record<ComparisonType, () => QueryValue> = {
      CONTAINS: () => ({ value: String(value), comparisonType: "CONTAINS" }),
      BETWEEN: () => ({ value: String(value), comparisonType: "BETWEEN" }),
      BETWEEN_TS: () => ({
        value: convertDateRangeToEpoch(value),
        comparisonType: "BETWEEN_TS",
      }),
      BETWEEN_TS_PRESET: () => {
        const options = Array.isArray(value) ? value : [];
        const firstId = options[0]?.id ?? options[0]?.name;
        const range =
          firstId && getPresetRange ? getPresetRange(String(firstId)) : "";
        return { value: range, comparisonType: "BETWEEN_TS" };
      },
      BY_NAME: () => ({
        value: Array.isArray(value)
          ? value.map((v: { name: string }) => v.name)
          : [],
        comparisonType: "EQUAL",
      }),
      ARRAY_SEARCH_ID: () => ({
        value: Array.isArray(value)
          ? value.map((v: { id: string }) => v.id)
          : [],
        comparisonType: "ARRAY_CONTAINS",
        arraySearchField: "id",
      }),
      ARRAY_CONTAINS: () => ({
        value: Array.isArray(value)
          ? value.map((v: { id: string }) => v.id)
          : [],
        comparisonType: "ARRAY_CONTAINS",
      }),
      BY_ID: () => ({
        value: Array.isArray(value)
          ? value.map((v: { id: string }) => v.id)
          : [],
      }),
      EQUAL: () => ({
        value: Array.isArray(value)
          ? value.map((v: { id: string }) => v.id)
          : [],
        comparisonType: "EQUAL",
      }),
      BOOLEAN: () => ({
        value: value.map((v: { name: string }) => v.name === "Yes"),
        comparisonType: "EQUAL",
      }),
      IN: () => ({
        value: Array.isArray(value)
          ? value.map((v: { id: string }) => v.id)
          : [],
        comparisonType: "IN",
      }),
      NOT_EQUAL_OR_NULL: () => ({
        value: Array.isArray(value)
          ? value.map((v: { id: string }) => v.id)
          : [],
        comparisonType: "NOT_EQUAL_OR_NULL",
      }),
      NOT_IN: () => ({
        value: Array.isArray(value)
          ? value.map((v: { id: string }) => v.id)
          : [],
        comparisonType: "NOT_IN",
      }),
    };

    const queryValue = comparisonMap[comparison]
      ? comparisonMap[comparison]()
      : comparisonMap["BY_ID"]();

    if (setValueForQuery) {
      setValueForQuery((prev: Record<string, QueryValue>) => ({
        ...prev,
        [fieldName]: queryValue,
      }));
    }
  };

  const handleFormReset = () => {
    clearSelections();

    setFilterValues(() =>
      persistentFilter?.reduce(
        (acc, key) => {
          acc[key] = [];
          return acc;
        },
        {} as Record<string, any[]>,
      ) ?? {},
    );

    setValueForQuery?.({});
  };

  const visibleFilters = filtersData.filter(
    (f) => f.type !== "hidden",
  );
  const isSingleFilter = visibleFilters.length === 1;
  const filterCount = visibleFilters.length;
  const gridLayout =
    filterCount === 2
      ? "grid grid-cols-1 sm:grid-cols-2 formsection gap-4 w-full"
      : filterCount === 3
        ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 formsection gap-4 w-full"
        : filterCount >= 4
          ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 formsection gap-4 w-full"
          : "";

  return (
    <div className="catalog-filters flex formsection flex-col w-full border p-6">
      <div
        className={
          isSingleFilter
            ? "flex flex-col md:flex-row md:items-end md:gap-4 w-full"
            : gridLayout
        }
      >
        {filtersData.map((f) => (
          <div
            className={
              isSingleFilter ? "flex-1 w-full md:min-w-0" : "min-w-0 w-full"
            }
            key={f.name}
          >
            {f.type === "custom" ? (
              f.render?.() ?? null
            ) : f.type === "text" ? (
              <Input
                key={f.name}
                title={f.customTitle || `By ${f.name}:`}
                name={f.name}
                value={filterValues?.[f.name] ?? ""}
                onChange={(e) =>
                  handleFilters(
                    f.name,
                    e.target.value,
                    f.fieldName,
                    f.comparison,
                  )
                }
                error={false}
              />
            ) : f.type === "date" ? (
              <CalendarInput
                key={f.name}
                title={f.customTitle || `By ${f.name}:`}
                error={false}
                type={"range"}
                value={filterValues?.[f.name] ?? ""}
                onChange={(e) =>
                  handleFilters(f.name, e, f.fieldName, f.comparison)
                }
              />
            ) : f.type === "hidden" ? null : (
              <DDMultiple
                key={f.name}
                title={f.customTitle || `By ${f.name}:`}
                objects={f.objectData || []}
                selectedObjst={filterValues?.[f.name] ?? []}
                seter={(e) =>
                  handleFilters(
                    f.name,
                    e,
                    f.fieldName,
                    f.comparison,
                    f.getPresetRange,
                  )
                }
                disabled={f.disabled}
                disableWrite={f.disabledWrite}
                disableSort={f.disableSort}
              />
            )}
          </div>
        ))}
        {isSingleFilter && (
          <div className="flex items-end justify-end md:justify-end mt-4 md:mt-0">
            <CatalogFilterButtons
              handleFormReset={handleFormReset}
            />
          </div>
        )}
      </div>
      {!isSingleFilter && (
        <div className="flex items-end justify-end">
          <CatalogFilterButtons
            handleFormReset={handleFormReset}
          />
        </div>
      )}
    </div>
  );
};

export default Filters;
